import React, { useState, useEffect } from 'react';
import {
  Users,
  AlertTriangle,
  FileText,
  Clock,
  Calendar,
  MailCheck
} from 'lucide-react';
import { useTabPersistence } from '../../hooks/usePersistence';
import useOvertimeData from './hooks/useOvertimeData';
import OvertimeStatsCards from './components/OvertimeStatsCards';
import EmployeeOvertimeTable from './components/EmployeeOvertimeTable';
import EmployeeOvertimeDetailModal from './components/EmployeeOvertimeDetailModal';
import OvertimeAlerts, { countOvertimeActionItems } from './components/OvertimeAlerts';
import ComplianceReport from './components/ComplianceReport';
import NotificationCoverage from './components/NotificationCoverage';
import { CountBadge } from '../../components/UI/CountBadge';
import PillSelect from '../../components/UI/PillSelect';
import ProcessingModal from '../../components/UI/ProcessingModal';

// Rotating status messages for the long-running compute on first paint /
// month change. Mirrors what the backend is actually doing (see
// staff_core/services/overtime_monitoring_service.py and the page footer):
//   - AR-sourced cap-eligible OT pull
//   - live LCS overlay for days that haven't been normalised yet
//   - 72h forecast loop (current month only)
//   - undeclared-OT discrepancy detection
// Order matters: shown sequentially every `rotateInterval` ms, so put the
// fastest steps first and the longer ones later — this keeps the
// elapsed-time chip + status text feeling roughly aligned with reality.
const OT_LOADING_MESSAGES = [
  'Pulling cap-eligible overtime from attendance records...',
  'Normalising live clock events for in-progress days...',
  'Running 72-hour forecast for the month...',
  'Detecting under-declared overtime...',
  'Building the employee roster...',
];

// Empirical expected duration for the OT roster compute, used by the
// progress modal to calibrate its asymptotic % curve. Anchoring this
// to observed backend timing (~60 s for a busy month with the AR
// kernel + live LCS overlay + forecast loop) keeps the displayed
// percentage in sync with what the admin sees ticking by in the
// server log. Past this point the curve decelerates toward 95% so
// the user can still tell "longer than usual" from "stuck".
const OT_LOADING_EXPECTED_MS = 60000;

// Grace period before the modal is allowed to mount. Cached/fast loads
// (e.g. tab return, repeat month switch within the session) typically
// resolve in <300ms — we don't want the modal to flash for those. The
// underlying skeleton still renders immediately, so there's no
// perceived stall.
const MODAL_DELAY_MS = 400;

// Format "YYYY-MM" -> "April 2026" for the modal subtitle.
const formatMonthLabel = (yearMonth) => {
  if (!yearMonth) return '';
  try {
    const [y, m] = yearMonth.split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
  } catch (e) {
    return yearMonth;
  }
};

/**
 * OvertimeMonitoring - 72-Hour Overtime Compliance Monitoring Page
 *
 * Admin page for HR to monitor and manage MOM 72-hour overtime compliance.
 * Employee Tracking + Alerts share the live AR/LCS employees snapshot.
 * Coverage audits digest delivery; Reports remains a separate export path.
 */
const OvertimeMonitoring = () => {
  const [activeTab, setActiveTab] = useTabPersistence('overtime-monitoring', 'employees', ['employees', 'alerts', 'reports', 'coverage']);
  
  const {
    stats,
    employees,
    allEmployees,
    forecastSummary,
    loading,
    error,
    filters,
    selectedMonth,
    isCurrentMonth,
    monthOptions,
    cacheStatus,
    updateFilters,
    changeMonth,
    fetchEmployeesForMonth,
    getEmployeeHistory,
    getEmployeeDailyBreakdown,
    getComplianceReport,
    getNotificationCoverage
  } = useOvertimeData();

  // Modal state for employee detail view
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Delayed processing-modal visibility. We don't bind the modal directly
  // to `loading` because cached/fast loads would cause it to flash for a
  // few hundred ms. Gate it behind MODAL_DELAY_MS so quick loads stay
  // silent (the skeleton handles that) and only genuinely slow loads
  // surface the modal — which is exactly when the user benefits from
  // knowing why the page hasn't appeared yet.
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  useEffect(() => {
    if (!loading) {
      setShowLoadingModal(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowLoadingModal(true), MODAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  // Per the design-system reference (frontend/src/styles/design-system-reference.md
  // — ProcessingModal Developer Checklist item 3), the modal should
  // surface failure in-modal, not just via a toast or the page-level
  // banner. We only escalate to the modal-error state if the modal was
  // actually visible during the failing load — if a fast load errors
  // (under MODAL_DELAY_MS), the existing red banner + toast already
  // handle it, and popping a modal after the fact would be jarring.
  const [modalError, setModalError] = useState(null);
  useEffect(() => {
    if (loading) {
      setModalError(null);
      return;
    }
    if (error && showLoadingModal) {
      setModalError(error);
    }
  }, [loading, error, showLoadingModal]);

  // Tabs: Employee Tracking, Alerts (live action inbox), Reports, Coverage
  const tabs = [
    { 
      id: 'employees', 
      name: 'Employee Tracking', 
      icon: Users,
      count: allEmployees?.filter(e => (e.unifiedStatus || e.riskLevel) !== 'compliant').length || 0
    },
    { 
      id: 'alerts', 
      name: 'Alerts', 
      icon: AlertTriangle,
      count: countOvertimeActionItems(allEmployees)
    },
    { 
      id: 'reports', 
      name: 'Reports', 
      icon: FileText,
      count: 0
    },
    {
      id: 'coverage',
      name: 'Notification Coverage',
      icon: MailCheck,
      count: 0
    }
  ];

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'employees':
        return (
          <div className="space-y-6">
            {/* Stats Cards at top — click filters the table */}
            <OvertimeStatsCards 
              stats={stats} 
              loading={loading} 
              forecastSummary={forecastSummary} 
              isCurrentMonth={isCurrentMonth}
              onCardClick={(value) => updateFilters({ riskLevel: value })}
              activeFilter={filters.riskLevel}
            />
            
            {/* Employee Table */}
            <EmployeeOvertimeTable
              employees={employees}
              loading={loading}
              filters={filters}
              onFilterChange={updateFilters}
              onViewEmployee={(emp) => setSelectedEmployee(emp)}
              isCurrentMonth={isCurrentMonth}
              cacheStatus={cacheStatus}
              selectedMonth={selectedMonth}
              monthOptions={monthOptions}
              fetchEmployeesForMonth={fetchEmployeesForMonth}
            />
          </div>
        );

      case 'alerts':
        return (
          <OvertimeAlerts
            employees={allEmployees}
            loading={loading}
            isCurrentMonth={isCurrentMonth}
            onViewEmployee={(emp) => setSelectedEmployee(emp)}
          />
        );

      case 'reports':
        return (
          <ComplianceReport
            getComplianceReport={getComplianceReport}
            employees={employees}
            stats={stats}
            loading={loading}
          />
        );

      case 'coverage':
        return (
          <NotificationCoverage
            getNotificationCoverage={getNotificationCoverage}
            selectedMonth={selectedMonth}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">72-Hour Overtime Monitoring</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
          MOM Compliance • Real-time overtime tracking and alerts
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            {error}
          </p>
        </div>
      )}

      {/* Tab Navigation - Admin theme with red. The month selector sits one
          click away regardless of the active tab (employees / alerts / reports).
          It is kept OUTSIDE the horizontally-scrolling tab strip: on mobile the
          strip uses `overflow-x-auto`, which forces the computed `overflow-y` to
          `auto` as well (CSS spec), clipping any absolutely-positioned dropdown
          to the ~44px strip height. Nesting the PillSelect there hid its menu
          on narrow viewports — so it lives in a sibling, non-overflow element. */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4 md:mb-6">
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth flex-1 min-w-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-btn-admin ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
                {tab.count > 0 && (
                  <CountBadge count={tab.count} theme="admin" active={isActive} className="ml-1" />
                )}
              </button>
            );
          })}
        </div>

        <div className="w-full md:w-auto md:flex-shrink-0">
          <PillSelect
            options={monthOptions}
            value={selectedMonth || ''}
            onChange={(val) => changeMonth?.(val)}
            theme="admin"
            icon={Calendar}
            iconColor="text-red-600"
            className="w-full md:w-auto"
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {renderTabContent()}
      </div>

      {/* Footer Info */}
      <div className="text-xs text-gray-500 text-center pt-4 border-t border-gray-200">
        Data source: AttendanceRecord (cap-eligible) + live LCS preview for un-normalised days •
        Showing: {monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth} •
        Last updated: {new Date().toLocaleString('en-SG')}
      </div>

      {/* Employee OT Detail Modal */}
      <EmployeeOvertimeDetailModal
        isOpen={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
        isCurrentMonth={isCurrentMonth}
        selectedMonth={selectedMonth}
        getEmployeeDailyBreakdown={getEmployeeDailyBreakdown}
        getEmployeeHistory={getEmployeeHistory}
      />

      {/* Long-running compute modal — surfaces while the AR-sourced
          overtime kernel + live LCS overlay + forecast are still
          building the month snapshot. Non-dismissible by design while
          loading: there's nothing meaningful to interact with on the
          page until the snapshot resolves, and a cancellable modal
          would imply we'd show stale data on cancel (we don't). On
          failure, the modal flips to the error state with a Dismiss
          button per the design-system ProcessingModal contract. */}
      <ProcessingModal
        isOpen={showLoadingModal || !!modalError}
        title="Loading Overtime Monitoring"
        subtitle={`${formatMonthLabel(selectedMonth)} • Computing 72-hour cap status`}
        theme="admin"
        statusMessages={OT_LOADING_MESSAGES}
        rotateInterval={6000}
        estimatedDurationMs={OT_LOADING_EXPECTED_MS}
        icon={<Clock className="w-7 h-7" />}
        error={modalError}
        onDismissError={() => setModalError(null)}
      />
    </div>
  );
};

export default OvertimeMonitoring;

